import React, { useState } from 'react';
import { useLinkedIn } from '../../context/LinkedInContext';
import BaseModal from '../modals/BaseModal';
import SafeIcon from '../../common/SafeIcon';

const AddProspectModal = ({ isOpen, onClose }) => {
  const { addProspect, createCompanyResearchProspect } = useLinkedIn();
  const [prospectType, setProspectType] = useState('person');
  const [formData, setFormData] = useState({
    linkedin_url: '',
    full_name: '',
    title: '',
    company_name: '',
    priority: 'Medium',
    notes: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const isResearch = prospectType === 'research';
    const companyName = formData.company_name.trim();

    if (isResearch) {
      if (!companyName) {
        setError('Selskapsnavn er påkrevd for research-prospekt.');
        return;
      }

      try {
        const result = await createCompanyResearchProspect({
          companyName
        });

        if (result?.duplicate) {
          setError('LinkedIn-prospect finnes allerede');
          return;
        }

        if (result?.success === false || result?.error) {
          setError(result.error || 'Kunne ikke lagre research-prospektet.');
          return;
        }

        if (result?.success === true) {
          onClose();
          setProspectType('person');
          setFormData({
            linkedin_url: '',
            full_name: '',
            title: '',
            company_name: '',
            priority: 'Medium',
            notes: ''
          });
          return;
        }

        setError('Kunne ikke lagre research-prospektet.');
      } catch (submitError) {
        setError('Kunne ikke lagre research-prospektet.');
      }

      return;
    }

    const linkedinUrl = formData.linkedin_url.trim();
    const isValidUrl = /^https:\/\/(www\.)?linkedin\.com\/in\/[^/?#]+\/?$/i.test(linkedinUrl);

    if (!formData.full_name.trim()) {
      setError('Navn er påkrevd.');
      return;
    }

    if (!isValidUrl) {
      setError('Skriv inn en gyldig LinkedIn-profil-URL.');
      return;
    }

    try {
      const result = await addProspect(formData);

      if (result?.success === false) {
        setError(result.error || 'Kunne ikke lagre prospektet.');
        return;
      }

      if (result?.error) {
        setError(result.error);
        return;
      }

      if (result?.success === true) {
        onClose();
        setProspectType('person');
        setFormData({
          linkedin_url: '',
          full_name: '',
          title: '',
          company_name: '',
          priority: 'Medium',
          notes: ''
        });
        return;
      }

      setError('Kunne ikke lagre prospektet.');
    } catch (submitError) {
      setError('Kunne ikke lagre prospektet.');
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Legg til LinkedIn-prospekt"
      disableAnimation={true}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Prospekttype</label>
          <select
            className="w-full px-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500 bg-white"
            value={prospectType}
            onChange={(e) => {
              setProspectType(e.target.value);
              setError('');
            }}
          >
            <option value="person">Personprospekt</option>
            <option value="research">Research / finn kontaktperson</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">LinkedIn URL</label>
          <div className="relative">
            <SafeIcon icon="🔗" className="absolute left-3 top-3 text-slate-400" />
            <input 
              required={prospectType === 'person'}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
              placeholder="https://linkedin.com/in/..."
              value={formData.linkedin_url}
              onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })} 
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Navn</label>
            <input 
              required={prospectType === 'person'}
              className="w-full px-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} 
            />
          </div>
          <div>
            <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Tittel</label>
            <input 
              className="w-full px-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Selskap</label>
          <input 
            required={prospectType === 'research'}
            className="w-full px-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Prioritet</label>
          <select 
            className="w-full px-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500 bg-white"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
          >
            <option>Lav</option>
            <option>Medium</option>
            <option>Høy</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Notater</label>
          <textarea 
            rows="3"
            className="w-full px-4 py-2 border border-slate-200 rounded-xl outline-none focus:border-blue-500"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          />
        </div>

        <p className="text-xs text-slate-500">
          Dette oppretter bare prospekt og oppgave. Ingen LinkedIn-melding sendes automatisk.
        </p>

        {error && (
          <p className="text-sm font-semibold text-rose-600" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end pt-4">
          <button type="submit" className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg">
            Lagre & Opprett Oppgaver
          </button>
        </div>
      </form>
    </BaseModal>
  );
};

export default AddProspectModal;